import { ExpandablePanel } from '@dealstack/frontend/shared/components/ExpandablePanel/ExpandablePanel';
import {
	generatePerformanceRatchetTierDescription,
	generatePerformanceRatchetTierSummary,
} from '@dealstack/frontend/shared/components/VestingTerms/helpers/vesting-messages.ts';
import { PerformanceRatchetTierEdit } from '@dealstack/frontend/shared/components/VestingTerms/PerformanceVesting/PerformanceRatchetTiers/PerformanceRatchetTierEdit.tsx';
import { PerformanceRatchetTierSummaryStyles } from '@dealstack/frontend/shared/components/VestingTerms/PerformanceVesting/PerformanceRatchetTiers/PerformanceRatchetTiers.styles.tsx';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	PerformanceVestingHurdleRes,
	PerformanceVestingMetricType,
} from '@dealstack/shared/lib/generated';
import { Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { TierFormValues } from './PerformanceRatchetTierEdit.types';

interface Props {
	tier?: PerformanceVestingHurdleRes;
	metricType: PerformanceVestingMetricType;
	isCreating?: boolean;
	onSave: (values: TierFormValues) => void;
	onDelete?: () => void;
	onCancelCreate?: () => void;
}

export const PerformanceRatchetTier = ({
	tier,
	metricType,
	isCreating = false,
	onSave,
	onDelete,
	onCancelCreate,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const [isExpanded, setIsExpanded] = useState(false);

	const title = tier
		? generatePerformanceRatchetTierDescription(tCommonUi, tier, metricType)
		: '';

	const summary = useMemo(() => {
		if (!tier) {
			return undefined;
		}

		const summaryText = generatePerformanceRatchetTierSummary(tCommonUi, tier, metricType);

		if (!summaryText) {
			return undefined;
		}

		return (
			<PerformanceRatchetTierSummaryStyles>
				<Text fz={12} c="gray.9" className="performance-ratchet-tier-summary">
					{summaryText}
				</Text>
			</PerformanceRatchetTierSummaryStyles>
		);
	}, [tier, tCommonUi, metricType]);

	return (
		<ExpandablePanel
			title={title}
			isExpanded={isCreating || isExpanded}
			onExpandClick={() => setIsExpanded(true)}
			titleItem={summary}
			cardActions={
				tier
					? [
							{
								title: tCommonUi('vesting_terms.make_changes'),
								onClick: () => setIsExpanded(true),
								intent: 'normal',
							},
							...(onDelete
								? [
										{
											title: tCommonUi('vesting_terms.remove'),
											onClick: onDelete,
											intent: 'danger' as const,
										},
									]
								: []),
						]
					: []
			}
		>
			<PerformanceRatchetTierEdit
				metricType={metricType}
				initialValues={
					tier
						? {
								moicThreshold: tier.moicThreshold,
								xirrThreshold: tier.xirrThreshold,
								incrementalPercentage: tier.incrementalPercentage,
							}
						: undefined
				}
				onSave={(values) => {
					onSave(values);
					setIsExpanded(false);
				}}
				onCancel={() => {
					if (isCreating) {
						onCancelCreate?.();
					} else {
						setIsExpanded(false);
					}
				}}
			/>
		</ExpandablePanel>
	);
};
